import { useMemo, useState, type CSSProperties } from "react";
import {
  lexbaseOpen,
  lexbasePreview,
  type LexbasePackage,
} from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT = "#b68235";
const ACCENT_100 = "#fff3e4";
const MUTED = "#7d7979";
const BG = "#f3f2f2";

type Step = 1 | 2 | 3 | 4;

/**
 * Lexbase / OP_CLEAN add-on wizard
 * Calls POST /v1/legal/lexbase/preview|open
 */
export default function Lexbase() {
  const [step, setStep] = useState<Step>(1);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [altNames, setAltNames] = useState("");
  const [protectedResidence, setProtectedResidence] = useState(false);
  const [shotsOk, setShotsOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pack, setPack] = useState<LexbasePackage | null>(null);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const altList = useMemo(
    () =>
      altNames
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [altNames],
  );

  const canPreview = fullName.trim().length >= 2 && email.includes("@") && shotsOk;

  async function onPreview() {
    setError(null);
    setBusy(true);
    try {
      const res = await lexbasePreview({
        fullName: fullName.trim(),
        email: email.trim(),
        protectedResidence,
        altNames: altList,
      });
      setPack(res.data);
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kunde inte generera mall");
    } finally {
      setBusy(false);
    }
  }

  async function onOpen() {
    setError(null);
    setBusy(true);
    try {
      const res = await lexbaseOpen({
        fullName: fullName.trim(),
        email: email.trim(),
        protectedResidence,
        altNames: altList,
      });
      setPack(res.data.ticket.package);
      setCaseId(res.data.ticket.caseId);
      setTicketId(res.data.ticket.id);
      setStep(4);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kunde inte öppna ärende");
    } finally {
      setBusy(false);
    }
  }

  function copyText(text: string) {
    void navigator.clipboard.writeText(text);
  }

  return (
    <div style={{ fontFamily: '"Lora", Georgia, serif', color: "#201f1d", maxWidth: 820 }}>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: ACCENT,
        }}
      >
        Add-on · Rättsdatabas
      </p>
      <h1
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontWeight: 400,
          fontSize: 34,
          color: NAVY,
          margin: "8px 0 8px",
        }}
      >
        Lexbase / OP_CLEAN
      </h1>
      <p style={{ color: MUTED, marginTop: 0, lineHeight: 1.55 }}>
        Generera formell GDPR-skrivelse till Legal Newsdesk (lexbase.se) enligt C-199/24. Mall —
        inte juridisk rådgivning. Skicka aldrig personnummer eller adress i okrypterad e-post.
      </p>

      {/* Steps */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "20px 0 28px" }}>
        {(
          [
            [1, "Checklista"],
            [2, "Uppgifter"],
            [3, "Förhandsgranska"],
            [4, "Skicka"],
          ] as const
        ).map(([n, label]) => (
          <span
            key={n}
            style={{
              padding: "6px 12px",
              borderRadius: 99,
              fontSize: 13,
              border: `1px solid ${step === n ? NAVY : "#d7d3d3"}`,
              background: step === n ? ACCENT_100 : BG,
              color: step === n ? NAVY : MUTED,
            }}
          >
            {n}. {label}
          </span>
        ))}
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            border: "1px solid #B42318",
            borderRadius: 6,
            color: "#B42318",
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Step 1 */}
      {step === 1 && (
        <section style={card}>
          <h2 style={h2}>1. Checklista före utskick</h2>
          <ul style={{ lineHeight: 1.7, color: MUTED, paddingLeft: 20 }}>
            <li>Ta daterade skärmdumpar av berörda Lexbase-sidor <strong>före</strong> mejl</li>
            <li>Spara kopia av utgående mejl</li>
            <li>Inga personnummer eller adress i klartext i e-post</li>
            <li>Begär mottagningsbekräftelse inom 7 dagar</li>
          </ul>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={shotsOk}
              onChange={(e) => setShotsOk(e.target.checked)}
              style={{ marginTop: 4 }}
            />
            <span>Jag har (eller ska ta) daterade skärmdumpar innan jag skickar</span>
          </label>
          <button type="button" disabled={!shotsOk} onClick={() => setStep(2)} style={btnPrimary(!shotsOk)}>
            Fortsätt
          </button>
        </section>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <section style={card}>
          <h2 style={h2}>2. Dina uppgifter</h2>
          <label style={label}>
            Fullständigt namn
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={input} placeholder="För- och efternamn" />
          </label>
          <label style={label}>
            E-post (kontakt i ärendet)
            <input value={email} onChange={(e) => setEmail(e.target.value)} style={input} type="email" placeholder="namn@exempel.se" />
          </label>
          <label style={label}>
            Alternativa namn (kommaseparerat, valfritt)
            <input value={altNames} onChange={(e) => setAltNames(e.target.value)} style={input} placeholder="t.ex. tidigare efternamn" />
          </label>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "16px 0", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={protectedResidence}
              onChange={(e) => setProtectedResidence(e.target.checked)}
              style={{ marginTop: 4 }}
            />
            <span>
              Jag har <strong>skyddad folkbokföring</strong> (16 § FBL) — lägg till förstärkande stycke. Handlingar via säker
              kanal, inte i mejl.
            </span>
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setStep(1)} style={btnGhost}>
              Tillbaka
            </button>
            <button type="button" disabled={!canPreview || busy} onClick={() => void onPreview()} style={btnPrimary(!canPreview || busy)}>
              {busy ? "Genererar…" : "Förhandsgranska mall"}
            </button>
          </div>
        </section>
      )}

      {/* Step 3 */}
      {step === 3 && pack && (
        <section style={card}>
          <h2 style={h2}>3. Förhandsgranska</h2>
          <p style={{ color: MUTED, fontSize: 14 }}>
            Mottagare: <strong>{pack.to}</strong> · Svarsfrist: <strong>{pack.deadline}</strong>
            {pack.protectedResidence ? " · Skyddad FB: ja" : ""}
          </p>
          <h3 style={{ ...h2, fontSize: 18 }}>Följebrev (e-post)</h3>
          <pre style={pre}>{pack.followUpEmail.body}</pre>
          <button type="button" onClick={() => copyText(pack.followUpEmail.body)} style={btnGhost}>
            Kopiera följebrev
          </button>
          <h3 style={{ ...h2, fontSize: 18, marginTop: 24 }}>Full skrivelse</h3>
          <pre style={{ ...pre, maxHeight: 280 }}>{pack.fullLetter}</pre>
          <button type="button" onClick={() => copyText(pack.fullLetter)} style={btnGhost}>
            Kopiera skrivelse
          </button>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
            <button type="button" onClick={() => setStep(2)} style={btnGhost}>
              Ändra uppgifter
            </button>
            <button type="button" disabled={busy} onClick={() => void onOpen()} style={btnPrimary(busy)}>
              {busy ? "Sparar…" : "Skapa ärende & gå till skicka"}
            </button>
          </div>
          <p style={{ fontSize: 12, color: MUTED, marginTop: 16 }}>{pack.disclaimer}</p>
        </section>
      )}

      {/* Step 4 */}
      {step === 4 && pack && (
        <section style={card}>
          <h2 style={h2}>4. Skicka till Lexbase</h2>
          {caseId && (
            <p style={{ fontSize: 15 }}>
              Ärende-ID: <strong style={{ fontFeatureSettings: '"tnum" 1' }}>{caseId}</strong>
              {ticketId ? <span style={{ color: MUTED }}> ({ticketId})</span> : null}
            </p>
          )}
          <ol style={{ lineHeight: 1.7, color: MUTED, paddingLeft: 20 }}>
            {(pack.checklist || []).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ol>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
            <a href={pack.mailto} style={{ ...btnPrimary(false), textDecoration: "none" }}>
              Öppna e-post till info@lexbase.se
            </a>
            <button type="button" onClick={() => copyText(pack.followUpEmail.subject)} style={btnGhost}>
              Kopiera ämnesrad
            </button>
          </div>
          <p style={{ fontSize: 13, color: MUTED, marginTop: 20, lineHeight: 1.55 }}>
            Efter utskick: spara mejlet, vänta på ärendenummer, notera svarsfrist{" "}
            <strong>{pack.deadline}</strong>. Vid avslag eller uteblivet svar — se IMY art. 77 (kommande steg i
            appen).
          </p>
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setPack(null);
              setCaseId(null);
              setTicketId(null);
              setShotsOk(false);
            }}
            style={{ ...btnGhost, marginTop: 12 }}
          >
            Nytt ärende
          </button>
        </section>
      )}
    </div>
  );
}

const card: CSSProperties = {
  background: "#fff",
  border: "1px solid #d7d3d3",
  borderRadius: 8,
  padding: 22,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};
const h2: CSSProperties = {
  fontFamily: '"Cormorant Garamond", Georgia, serif',
  fontWeight: 400,
  fontSize: 22,
  color: NAVY,
  margin: "0 0 12px",
};
const label: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 13,
  marginBottom: 12,
  color: NAVY,
};
const input: CSSProperties = {
  padding: "11px 12px",
  border: "1px solid #d7d3d3",
  borderRadius: 4,
  fontSize: 15,
  fontFamily: "inherit",
};
const pre: CSSProperties = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: BG,
  border: "1px solid #d7d3d3",
  borderRadius: 6,
  padding: 14,
  fontSize: 12.5,
  lineHeight: 1.45,
  maxHeight: 220,
  overflow: "auto",
  fontFamily: "ui-monospace, Consolas, monospace",
};
function btnPrimary(disabled: boolean): CSSProperties {
  return {
    marginTop: 16,
    padding: "11px 20px",
    border: `1px solid ${NAVY}`,
    borderRadius: 4,
    background: disabled ? "#eae9e9" : "transparent",
    color: NAVY,
    fontSize: 14.5,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };
}
const btnGhost: CSSProperties = {
  marginTop: 16,
  padding: "11px 16px",
  border: "1px solid #d7d3d3",
  borderRadius: 4,
  background: "transparent",
  color: MUTED,
  fontSize: 14,
  cursor: "pointer",
};
